import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { PaymentsKanban } from '@/features/payments/components/PaymentsKanban';
import { PaymentsReport } from '@/features/payments/components/PaymentsReport';
import { MonthYearSelector } from '@/features/payments/components/MonthYearSelector';
import { PaymentDateExportDialog } from '@/features/payments/components/PaymentDateExportDialog';
import { paymentsService } from '@/features/payments/services/payments.service';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/_authenticated/employees/payments')({
  component: PaymentsPage,
});

function PaymentsPage() {
  const { toast } = useToast();
  const [month, setMonth] = useState(() => format(new Date(), 'yyyy-MM'));
  const [view, setView] = useState<'kanban' | 'report'>('kanban');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [pendingExport, setPendingExport] = useState<{ type: 'company'; companyId: string } | { type: 'general' } | null>(null);

  const { data: report, isLoading: loadingReport } = useQuery({
    queryKey: ['payments-report', month],
    queryFn: () => paymentsService.getReport(month),
  });

  const handleExportByCompany = (companyId: string) => {
    setPendingExport({ type: 'company', companyId });
    setExportDialogOpen(true);
  };

  const handleExportGeneral = () => {
    setPendingExport({ type: 'general' });
    setExportDialogOpen(true);
  };

  const handleExportConfirm = async (paymentDate: string) => {
    if (!pendingExport) return;
    try {
      let blob: Blob;
      let filename: string;
      if (pendingExport.type === 'company') {
        blob = await paymentsService.exportXlsxByCompany(month, pendingExport.companyId, paymentDate);
        filename = `pagamentos-${month}-empresa-${pendingExport.companyId}.xlsx`;
      } else {
        blob = await paymentsService.exportXlsxGeneral(month, paymentDate);
        filename = `pagamentos-${month}-geral.xlsx`;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setPendingExport(null);
      toast({ title: 'Planilha exportada com sucesso' });
    } catch {
      toast({
        title: 'Erro ao exportar planilha',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Pagamentos</h1>
          <p className="text-muted-foreground">
            Gestão de pagamentos mensais de prestadores
          </p>
        </div>
        {view === 'report' && (
          <MonthYearSelector value={month} onChange={setMonth} />
        )}
      </div>

      <Tabs value={view} onValueChange={(v) => setView(v as 'kanban' | 'report')}>
        <TabsList>
          <TabsTrigger value="kanban">Kanban</TabsTrigger>
          <TabsTrigger value="report">Relatório</TabsTrigger>
        </TabsList>
        <TabsContent value="kanban">
          <PaymentsKanban month={month} onMonthChange={setMonth} />
        </TabsContent>
        <TabsContent value="report">
          <PaymentsReport
            report={report || { companies: [], noCompany: [] }}
            month={month}
            onExportByCompany={handleExportByCompany}
            onExportGeneral={handleExportGeneral}
            isLoading={loadingReport}
          />
        </TabsContent>
      </Tabs>

      <PaymentDateExportDialog
        open={exportDialogOpen}
        onOpenChange={(open) => {
          setExportDialogOpen(open);
          if (!open) setPendingExport(null);
        }}
        onConfirm={handleExportConfirm}
        month={month}
        title={pendingExport?.type === 'company' ? 'Exportar planilha da empresa' : 'Exportar planilha geral'}
      />
    </div>
  );
}
